import { Size, partsDealProblem, serviceRequestOptionsPath, t } from '@bms2/shared';
import { ItemTable, newLine, type ItemLineDraft } from '../../components/composites/ItemTable';
import { DatePicker, Dropdown, Select, TextInput, Textarea } from '../../components/controls';
import modalStyles from '../../components/modals/modal.module.css';
import { useItemPicker } from '../settings/api';
import { useApi } from './api';
import { useAddNew } from './AddNewHost';
import { useEmploymentSearch } from './DealModals';
import styles from './PartsDealFields.module.css';

/** 🔨 Relative to the API's own root, never prefixed with /api (piece 1). The Parts form's save (decision 2290). */
export const PARTS_DEALS_PATH = '/deals/parts';

/** The Parts form's answers as it holds them (piece 18, *New Deal by type*; decisions 2290, 2390). */
export interface PartsDealDraftState {
  askedById: string | null;
  askedByName: string | null;
  /** The owner picked in place of the person saving, or null where the person saving owns it. */
  ownerId: string | null;
  ownerName: string | null;
  /** A calendar day, written year-month-day, or empty. */
  expectedDate: string;
  lines: ItemLineDraft[];
  shipToSiteId: string;
  customerPoNumber: string;
  need: string;
}

/** 🔨 One empty line, so a pick fills it: the account, who asked, the part and Save are the four actions (piece 18). */
export function emptyPartsDeal(): PartsDealDraftState {
  return { askedById: null, askedByName: null, ownerId: null, ownerName: null, expectedDate: '', lines: [newLine(false)], shipToSiteId: '', customerPoNumber: '', need: '' };
}

/**
 * 🔒 The body the Parts route takes (decision 2290). A line's price goes only
 * where the person changed the one the form showed, so an untouched line is
 * priced as a generated line is, the account's price sheet first (decision 0440).
 * A problem the shared rule names is said before anything is sent.
 */
export function partsDealBody(values: Record<string, unknown>, draft: PartsDealDraftState, listPriceOf: (itemId: string) => number | null): { ok: true; body: Record<string, unknown> } | { ok: false; problem: string } {
  const companyId = typeof values.companyId === 'string' && values.companyId ? values.companyId : null;
  const lines = draft.lines.filter((line) => line.kind !== 'divider' && (line.itemId || line.itemLabel.trim() || line.description.trim()));
  const problem = partsDealProblem({ companyId, requestedByEmploymentId: draft.askedById, expectedDate: draft.expectedDate || null, lines: lines.map((line) => ({ itemId: line.itemId, quantity: line.qty })) });
  if (problem) return { ok: false, problem: `${problem.charAt(0).toUpperCase()}${problem.slice(1)}.` };
  return {
    ok: true,
    body: {
      companyId,
      requestedByEmploymentId: draft.askedById,
      ...(draft.ownerId ? { ownerEmploymentId: draft.ownerId } : {}),
      expectedDate: draft.expectedDate || null,
      shipToSiteId: draft.shipToSiteId || null,
      customerPoNumber: draft.customerPoNumber.trim() || null,
      need: draft.need.trim() || null,
      lines: lines.map((line) => {
        const shown = line.itemId ? listPriceOf(line.itemId) : null;
        const changed = line.rateCents !== null && line.rateCents !== shown;
        // 🔒 The line's unit is set in the item table's unit cell: a chosen override rides here, and none leaves the Item's own unit of sale to the server (decision 0439; T1699).
        return { itemId: line.itemId, quantity: line.qty, unitId: line.unitId ?? null, ...(line.description.trim() ? { description: line.description.trim() } : {}), ...(changed ? { unitPriceCents: line.rateCents } : {}) };
      }),
    },
  };
}

/**
 * The Parts form (piece 18, *New Deal by type*; decisions 2290, 2390), drawn
 * beneath the Account and the Type once Parts is the type: who asked, the owner,
 * the Expected date drawn prominently, the parts, the ship-to, the customer's
 * purchase order number and the optional *In their words* line. 🔨 It asks no
 * name, value, stage or route: a Parts deal saves open at Evaluation on the
 * Quick route, and its accepted Quote wins it.
 */
export function PartsDealFields({ companyId, draft, onChange, apiBaseUrl = '', picker }: { companyId: string | null; draft: PartsDealDraftState; onChange: (next: PartsDealDraftState) => void; apiBaseUrl?: string; picker: ReturnType<typeof useItemPicker> }) {
  // The account's open sites, read from the request form's own options (decision 1912), which list them for a Deal reader.
  const options = useApi<{ sites?: { id: string; label: string }[] }>(companyId ? serviceRequestOptionsPath(companyId, null) : null, apiBaseUrl);
  const sites = options.state.kind === 'ready' && Array.isArray(options.state.data.sites) ? options.state.data.sites : [];
  // 🔨 The Account's people and nobody else (decision 0328), as the contact picker lists them.
  const searchPeople = useEmploymentSearch(apiBaseUrl, companyId, null, 'account');
  // 🔒 The owner is somebody employed here: the staff scope lists nobody else.
  const searchStaff = useEmploymentSearch(apiBaseUrl, null, null, 'staff');
  const addNew = useAddNew(apiBaseUrl);
  const set = (patch: Partial<PartsDealDraftState>) => onChange({ ...draft, ...patch });
  const { itemSearch, units, prices, skus, descriptions, quantityRules } = picker;
  // 🔨 The only site is the ship-to the Quote would take anyway, so it is shown chosen rather than guessed at the save.
  const shipTo = draft.shipToSiteId || (sites.length === 1 ? sites[0]!.id : '');
  return (
    <section className={modalStyles.form} data-parts-deal-fields="true">
      <p className={modalStyles.lead}>{companyId ? t('deal.parts.formHint') : t('deal.parts.pickAccount')}</p>
      <span data-parts-deal-field="askedBy">
        <Dropdown size={Size.Medium} label={t('deal.parts.askedBy')} entityLabel={t('deal.parts.askedByEntity')} search={searchPeople} value={draft.askedByName} onSelect={(row) => set({ askedById: row.id, askedByName: row.line1 })} onAddNew={companyId ? () => addNew.open({ kind: 'contact', companyId, pick: 'employment' }, (row) => set({ askedById: row.id, askedByName: row.line1 })) : null} disabled={!companyId} required />
      </span>
      {/* 🔒 Drawn prominently near the top: the day the customer wants the parts can drive the sale (decision 2390). */}
      <div className={styles.expected} data-parts-deal-field="expectedDate">
        <DatePicker size={Size.Medium} mode="date" label={t('deal.parts.expectedDate')} value={draft.expectedDate} onChange={(expectedDate) => set({ expectedDate })} name="parts-expected-date" />
        <p className={styles.expectedHint}>{t('deal.parts.expectedDateHint')}</p>
      </div>
      <div className={styles.lines} data-parts-deal-field="lines">
        <span className={styles.linesLabel}>{t('deal.parts.lines')}</span>
        <ItemTable
          documentType="Quote"
          columns={['item', 'qty', 'unit', 'rate', 'amount']}
          lines={draft.lines}
          onChange={(lines) => set({ lines })}
          discountLevel="Footer"
          itemSearch={itemSearch}
          seedRateCents={(id) => prices.current.get(id) ?? null}
          unitOf={(id) => units.current.get(id) ?? null}
          skuOf={(id) => skus.current.get(id) || null}
          descriptionOf={(id) => descriptions.current.get(id) || null}
          quantityRuleOf={(id) => quantityRules.current.get(id) ?? null}
        />
      </div>
      <Select size={Size.Medium} label={t('deal.parts.shipTo')} value={shipTo} onChange={(shipToSiteId) => set({ shipToSiteId })} placeholder={t('edit.none')} options={sites.map((row) => ({ value: row.id, label: row.label }))} disabled={!companyId} dataAttribute={{ 'data-parts-deal-field': 'shipTo' }} />
      <TextInput size={Size.Medium} label={t('deal.parts.customerPo')} value={draft.customerPoNumber} onChange={(customerPoNumber) => set({ customerPoNumber })} name="parts-customer-po" />
      <p className={modalStyles.lead}>{t('deal.parts.customerPoHint')}</p>
      <span data-parts-deal-field="owner">
        <Dropdown size={Size.Medium} label={t('deal.parts.owner')} entityLabel={t('deal.parts.ownerEntity')} search={searchStaff} value={draft.ownerName} onSelect={(row) => set({ ownerId: row.id, ownerName: row.line1 })} onAddNew={null} />
        {draft.ownerId ? null : <p className={modalStyles.lead} data-parts-deal-owner-hint="true">{t('deal.parts.ownerYou')}</p>}
      </span>
      <Textarea size={Size.Large} label={t('deal.parts.inTheirWords')} value={draft.need} onChange={(need) => set({ need })} name="parts-need" placeholder={t('deal.parts.inTheirWordsHint')} />
      {addNew.host}
    </section>
  );
}
